/**
 * roles.ts — centralised RBAC definitions for the Pelajari frontend.
 *
 * Role hierarchy (highest → lowest):
 *   super_admin (5) > admin (4) > hr (3) > manager (2) > employee (1)
 *
 * Utility functions:
 * - `hasRole(userRole, minimumRole)` — hierarchical check (>= level).
 *   Used by ProtectedRoute's `minimumRole` prop.
 * - `isOneOf(userRole, allowedRoles)` — strict allow-list check.
 *   Used by ProtectedRoute's `allowedRoles` prop.
 * - `can(userRole, permission)` — feature-level permission check.
 *   Used by route components to conditionally render management actions.
 *
 * Adding a new permission:
 * 1. Add the permission key to the `Permission` union type.
 * 2. Add the allowed roles array to the `PERMISSIONS` map.
 * 3. Call `can(user?.role, 'your:permission')` in the component.
 */
export type UserRole = 'super_admin' | 'admin' | 'hr' | 'manager' | 'employee';

export const ROLE_LEVELS: Record<UserRole, number> = {
  super_admin: 5,
  admin: 4,
  hr: 3,
  manager: 2,
  employee: 1,
};

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Company Admin',
  hr: 'HR',
  manager: 'Manager',
  employee: 'Employee',
};

export const ROLE_COLORS: Record<UserRole, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  hr: 'bg-green-100 text-green-800',
  manager: 'bg-yellow-100 text-yellow-800',
  employee: 'bg-stone-100 text-stone-700',
};

/** Returns true if the user's role meets or exceeds the minimum required role. */
export function hasRole(userRole: UserRole | string | undefined, minimumRole: UserRole): boolean {
  if (!userRole) return false;
  const userLevel = ROLE_LEVELS[userRole as UserRole] ?? 0;
  const requiredLevel = ROLE_LEVELS[minimumRole];
  return userLevel >= requiredLevel;
}

/** Returns true if the user's role is one of the allowed roles. */
export function isOneOf(userRole: UserRole | string | undefined, allowedRoles: UserRole[]): boolean {
  if (!userRole) return false;
  return allowedRoles.includes(userRole as UserRole);
}

export type Permission =
  | 'view:org'
  | 'manage:org'
  | 'invite:users'
  | 'manage:users'
  | 'view:kinerja'
  | 'manage:kinerja'
  | 'view:wawasan'
  | 'manage:wawasan'
  | 'view:rekrut'
  | 'manage:rekrut'
  | 'view:admin'
  | 'manage:admin';

const PERMISSIONS: Record<Permission, UserRole[]> = {
  'view:org':       ['super_admin', 'admin', 'hr', 'manager', 'employee'],
  'manage:org':     ['super_admin', 'admin', 'hr'],
  'invite:users':   ['super_admin', 'admin', 'hr'],
  'manage:users':   ['super_admin', 'admin'],
  'view:kinerja':   ['super_admin', 'admin', 'hr', 'manager', 'employee'],
  'manage:kinerja': ['super_admin', 'admin', 'hr'],
  'view:wawasan':   ['super_admin', 'admin', 'hr', 'manager'],
  'manage:wawasan': ['super_admin', 'admin', 'hr'],
  'view:rekrut':    ['super_admin', 'admin', 'hr', 'manager'],
  'manage:rekrut':  ['super_admin', 'admin', 'hr'],
  'view:admin':     ['super_admin', 'admin'],
  'manage:admin':   ['super_admin', 'admin'],
};

export function can(userRole: UserRole | string | undefined, permission: Permission): boolean {
  if (!userRole) return false;
  const allowed = PERMISSIONS[permission];
  return allowed.includes(userRole as UserRole);
}
